/**
 * Web to PDF: the URL form. A job converts the page (POST /api/v1/jobs with
 * `url`); the form reports success only when the job succeeded.
 */

import { trackConversion } from "./analytics";
import { createJob } from "./api/client";
import type { Inbox } from "./inbox";
import { announce } from "./lib/announce";
import { find, get, setMessage } from "./lib/dom";
import { formValue, isBusy, setBusy } from "./lib/forms";
import { errorMessage, isAbortError } from "./lib/http";
import { waitForJob } from "./converter/jobs";
import { isPremium, loadMe } from "./session";
import { openSignup, recordSuccessfulUpload, shouldNudge } from "./signup";

const INCORRECT_URL = "You have entered an incorrect URL";
const CONVERT_FAILED =
  "Cannot convert the URL to PDF. Please check the URL you entered.";
const MAX_URL_LENGTH = 2048;

/** The URL to convert: http(s) with a dotted host name; a missing scheme means http. */
export function normalizeUrl(raw: string): string | null {
  const text = raw.trim();
  if (text === "" || text.length > MAX_URL_LENGTH) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(text)
    ? text
    : `http://${text}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (!url.hostname.includes(".") || url.username !== "" || url.password !== "")
    return null;
  return url.href;
}

export function initWebToPdf(inbox: Inbox | null): void {
  const root = find(document, "[data-web-to-pdf]", HTMLElement);
  if (root === null) return;
  const form = get(root, "[data-web-form]", HTMLFormElement);
  const input = get(form, "input[name=url]", HTMLInputElement);
  const error = get(form, "[data-web-error]", HTMLElement);
  const status = get(form, "[data-web-status]", HTMLElement);
  const statusText = get(form, "[data-web-status-text]", HTMLElement);
  const tool = root.dataset["tool"] ?? "webtopdf";
  let premium = false;
  void loadMe().then((visitor) => {
    premium = isPremium(visitor);
  });

  const fail = (message: string): void => {
    setMessage(error, message);
    input.setAttribute("aria-invalid", "true");
    status.hidden = true;
  };

  const convert = async (url: string): Promise<void> => {
    setBusy(form, true);
    setMessage(error, "");
    input.removeAttribute("aria-invalid");
    statusText.textContent = "Please wait... Saving the web page to PDF";
    status.hidden = false;
    const controller = new AbortController();
    try {
      const job = await createJob(
        { tool, uploads: [], options: {}, url },
        controller.signal,
      );
      const done = await waitForJob(job.id, { signal: controller.signal });
      if (done.status !== "succeeded") {
        fail(done.error?.message ?? CONVERT_FAILED);
        return;
      }
      trackConversion(tool);
      status.hidden = true;
      form.reset();
      if (!premium) recordSuccessfulUpload();
      announce("The web page was saved to PDF. The file is in your Inbox.");
      void inbox?.refresh();
    } catch (failure) {
      if (!isAbortError(failure)) fail(errorMessage(failure));
    } finally {
      setBusy(form, false);
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (isBusy(form)) return;
    const url = normalizeUrl(formValue(form, "url"));
    if (url === null) {
      fail(INCORRECT_URL);
      input.focus();
      return;
    }
    if (!premium && shouldNudge()) {
      openSignup(() => void convert(url));
      return;
    }
    void convert(url);
  });
}
